import type { RequestIdManager } from '@funtech-int/yango-shared';
import { createFetcher } from '@funtech-int/web-toolkit';
import { createRef } from '@funtech-int/rival';

import type { Logger } from '@kinopoisk-int/logger';
import { RequestIdsHeader } from '@kinopoisk-int/request-ids';

import { fetcherLoggingMiddleware } from './fetcherMiddlewares';

interface LoggedInEventBody {
    type: 'UserLoggedInEvent';
}

interface BackendClient {
    postLoggedInEvent(body: LoggedInEventBody, headers?: Record<string, string>): Promise<void>;

    ref(): typeof backendClientRef;
}

export const backendClientRef = createRef<BackendClient>('backendClient');

interface Params {
    saftGatewayApi: string;
    requestIdManager: RequestIdManager;
    logger: Logger;
    lang: string;
}

function getDefaultHeaders(
    requestIdManager: RequestIdManager,
    baseUrl: string,
    lang: string
): Record<string, string> {
    const realHost = new URL(baseUrl).hostname;
    return {
        [RequestIdsHeader.RequestId]: requestIdManager.requestId.child,
        [RequestIdsHeader.Traceparent]: requestIdManager.traceparent,
        'x-real-host': realHost,
        'content-type': 'application/json',
        'X-Preferred-Language': lang,
    };
}

async function parseJsonResponse<T>(response: Response): Promise<T | null> {
    if (!response.ok) {
        throw new Error([response.status, response.statusText].filter(Boolean).join(' '));
    }
    const text = await response.text();
    return text.length > 0 ? (JSON.parse(text) as T) : null;
}

export function createBackendClient({
    saftGatewayApi,
    requestIdManager,
    logger,
    lang,
}: Params): BackendClient {
    const fetcher = createFetcher(
        {
            baseUrl: saftGatewayApi,
            timeout: 60000,
            credentials: 'include',
        },
        [fetcherLoggingMiddleware(logger, 'browser-backend-client')]
    );

    return {
        postLoggedInEvent(body, headers) {
            return fetcher
                .fetch('/v1/events/feedback', {
                    method: 'POST',
                    headers: {
                        ...getDefaultHeaders(requestIdManager, saftGatewayApi, lang),
                        ...headers,
                    },
                    body: JSON.stringify(body),
                })
                .then(parseJsonResponse<void>)
                .then(() => undefined);
        },

        ref() {
            return backendClientRef;
        },
    };
}
